import '@/App.css';
import './i18n';
import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Nav } from './site/Nav';
import { Hero } from './site/Hero';
import { Problem } from './site/Problem';
import { Services } from './site/Services';
import { About } from './site/About';
import { Packages } from './site/Packages';
import { CTAContact } from './site/CTAContact';
import { Footer } from './site/Footer';
import { Portfolio } from './site/Portfolio';
import { Blog } from './site/blog/Blog';
import { BlogPost } from './site/blog/BlogPost';
import { AuthProvider } from './site/admin/AuthContext';
import { AdminLogin } from './site/admin/AdminLogin';
import { AdminBlogList } from './site/admin/AdminBlogList';
import { AdminBlogEditor } from './site/admin/AdminBlogEditor';
import { LangMeta } from './site/LangMeta';
import { SUPPORTED_LANGS, detectInitialLang } from './i18n';

const Home = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const active = SUPPORTED_LANGS.includes(lang) ? lang : 'en';

  useEffect(() => {
    if (i18n.language !== active) i18n.changeLanguage(active);
  }, [active, i18n]);

  return (
    <main className="App relative">
      <LangMeta lang={active} />
      <div className="grain-fixed" aria-hidden />
      <Nav />
      <Hero />
      <Problem />
      <Services />
      <About />
      <Packages />
      <CTAContact />
      <Footer />
    </main>
  );
};

const PortfolioPage = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const active = SUPPORTED_LANGS.includes(lang) ? lang : 'en';

  useEffect(() => {
    if (i18n.language !== active) i18n.changeLanguage(active);
  }, [active, i18n]);

  return (
    <main className="App relative">
      <LangMeta lang={active} />
      <div className="grain-fixed" aria-hidden />
      <Nav />
      <Portfolio />
      <CTAContact />
      <Footer />
    </main>
  );
};

const BlogListPage = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const active = SUPPORTED_LANGS.includes(lang) ? lang : 'en';

  useEffect(() => {
    if (i18n.language !== active) i18n.changeLanguage(active);
  }, [active, i18n]);

  return (
    <main className="App relative">
      <LangMeta lang={active} />
      <div className="grain-fixed" aria-hidden />
      <Nav />
      <Blog />
      <CTAContact />
      <Footer />
    </main>
  );
};

const BlogPostPage = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const active = SUPPORTED_LANGS.includes(lang) ? lang : 'en';

  useEffect(() => {
    if (i18n.language !== active) i18n.changeLanguage(active);
  }, [active, i18n]);

  return (
    <main className="App relative">
      <LangMeta lang={active} />
      <div className="grain-fixed" aria-hidden />
      <Nav />
      <BlogPost />
      <CTAContact />
      <Footer />
    </main>
  );
};

const RootRedirect = () => {
  const detected = detectInitialLang();
  const location = useLocation();
  return <Navigate to={`/${detected}${location.hash || ''}`} replace />;
};

const ScrollHandler = () => {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait for the target section to mount, then scroll to it
      const id = hash.replace('#', '');
      let tries = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
        } else if (tries < 20) {
          tries += 1;
          setTimeout(tryScroll, 50);
        }
      };
      tryScroll();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, key]);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollHandler />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/blog" element={<AdminBlogList />} />
          <Route path="/admin/blog/new" element={<AdminBlogEditor mode="new" />} />
          <Route path="/admin/blog/:slug/edit" element={<AdminBlogEditor mode="edit" />} />
          <Route path="/:lang/portfolio" element={<PortfolioPage />} />
          <Route path="/:lang/blog" element={<BlogListPage />} />
          <Route path="/:lang/blog/:slug" element={<BlogPostPage />} />
          <Route path="/:lang/admin/login" element={<AdminLogin />} />
          <Route path="/:lang/admin/blog" element={<AdminBlogList />} />
          <Route path="/:lang/admin/blog/new" element={<AdminBlogEditor mode="new" />} />
          <Route path="/:lang/admin/blog/:slug/edit" element={<AdminBlogEditor mode="edit" />} />
          <Route path="/:lang" element={<Home />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
